import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getBackendURL, SETTINGS_PASSWORD, BASE_URLS } from '../../components/env';
import { fetchChatModels, chatModelOptions, coerceChatModel, defaultOcrModelsFrom } from '../../utils/chatModels';
import {
  isInGroup,
} from '../../config/roles';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import {
  loadAiChatSettings,
  saveAiChatSettings,
  normalizeAiChatSettings,
  DEFAULT_OCR_MODELS,
  CAVEMAN_INTENSITIES,
  skillBadgeLabel,
} from '../../utils/aiChatSettings';
import { beginActivity, endActivity } from '../../utils/activityTracker';
import VisitBriefChatStrip from './previsitBrief/VisitBriefChatStrip';
import { keepAttachedBriefs, noteRemovedBriefs, withAttachedBrief } from './previsitBrief/briefMessages';

// Source keys match the backend's expected identifiers (see AIAssistant.js
// + ai_assistant.py PatientDocumentProcessor.prepare_documents). The backend
// already loads each of these when present in `selectedSources`:
//   - 'forms'            → submitted form rows (StoringFormSubmission)
//   - 'audio_transcripts'→ saved audio + their transcripts
// 'uploaded_files' is the canonical key for "Reports" (uploaded report PDFs/files).
// `requiresFormsData` marks a chip that should be disabled until the patient
// actually has form submissions (mirrors AIAssistant.js's hasFormsData gate).
const ALL_SOURCES = [
  { key: 'patient_info',      label: 'Patient Info',  defaultOn: true },
  { key: 'medical_document',  label: 'Med Docs',      defaultOn: true },
  { key: 'ai_knowledge',      label: 'AI Knowledge',  defaultOn: true },
  { key: 'visit_history',     label: 'Visit History', defaultOn: false },
  { key: 'uploaded_files',    label: 'Reports',       defaultOn: false },
  { key: 'forms',             label: 'Forms',         defaultOn: false, requiresFormsData: true },
  { key: 'audio_transcripts', label: 'Audio',         defaultOn: false },
];

const SUGGESTED_PROMPTS = [
  'Recent labs?',
  'Medication list',
  'Cardiac history',
  'Allergy history',
  'Prior visits',
];

// Settings options (labels mirror the doctor-page AIAssistant).
const SPEED_OPTIONS = [
  { value: 'fast', label: 'Fast (5,000 chars/doc, 50,000 total)' },
  { value: 'medium', label: 'Medium (10,000 chars/doc, 100,000 total)' },
  { value: 'slow', label: 'Slow (1,000,000 chars/doc, 1,000,000 total)' },
  { value: 'advanced', label: 'Advanced (Custom values)' },
];
function nowHHmm() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function defaultGreeting(patientName) {
  const name = patientName || 'this patient';
  return `Hello! I'm your AI assistant for ${name}. I have access to their medical records, previous labs, visit history, and clinical documents. What would you like to know?`;
}

// Monotonic counter for client-side message IDs. Lets the pinned bar
// scroll to a specific exchange while DB feedback_id is still in flight.
let nextLocalId = 1;
const makeLocalId = () => `local_${nextLocalId++}`;

// Take a prompt's first non-empty line for the pinned bar label.
function firstLine(text) {
  if (!text) return '';
  const lines = String(text).split('\n').map(l => l.trim()).filter(Boolean);
  return lines[0] || '';
}

// The auto-fired labs-summary query — used both to fire the query and to
// detect whether the same query has already been asked in prior history
// (we never want to ask it twice). Detection matches on the distinctive
// "most recent lab results" phrase so future variations still match.
const LAB_SUMMARY_PROMPT_PATTERN = /most recent lab results/i;

// Pattern in `patient.reports` value indicating it points to a storage
// folder. A patient row's `reports` column either holds a folder path
// ("New/Reports/<name>_<id>/<date>") or is empty/null.
async function patientHasReports(patientRow) {
  const folderPath = patientRow?.reports;
  if (!folderPath || typeof folderPath !== 'string') return false;
  try {
    const sb = await getSupabase();
    // The clinic and doctor pages both read from the `files` bucket; use
    // the same. checkFilesExist in AIAssistant.js does more elaborate
    // recovery for moved folders — keep this lightweight (the auto-trigger
    // can be best-effort: false negatives just skip the auto-query).
    const { data, error } = await sb.storage.from('files').list(folderPath);
    if (error || !Array.isArray(data)) return false;
    return data.some((entry) => entry?.name && entry.name.includes('.'));
  } catch (_) {
    return false;
  }
}

// Patient has at least one submitted form? `submissions_array` is the
// notes-c column listing form submissions. Accepts either the raw row or
// the array shape patientData can take (mirrors AIAssistant.hasFormsData).
function patientHasForms(patientRow) {
  const p = Array.isArray(patientRow) ? patientRow[0] : patientRow;
  return Array.isArray(p?.submissions_array) && p.submissions_array.length > 0;
}

// Flatten ReactMarkdown's children (React elements + strings) into a single
// plain text string. Used by the custom `tr` renderer to inspect each row
// for the flag emojis (🚨 / ⚠️) and tag the <tr> with a color class.
function childrenToText(children) {
  if (children == null) return '';
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join('');
  if (children.props && children.props.children !== undefined) {
    return childrenToText(children.props.children);
  }
  return '';
}

export default function MedStudentAIAssistant({
  patientId,
  patientData,
  // Postgres schema this chart's patient lives in. For a cross-schema
  // (linked-provider) patient pulled from another schema, the clinic page
  // threads that schema down so AI threads / chat-history / query / pin-toggle
  // all read & write the RIGHT tenant's `ai_feedback` instead of the viewer's
  // home schema. Defaults to the active schema — unchanged for same-schema charts.
  schema = supabaseSchema,
  documentContent,
  documentPlainRef = null,
  userRole,
  scheduledDate,
  userInitials = 'YOU',
  injectPrompt = null,
  onSettingsSaved,
  // One-shot signal that the SAME per-user skill preference was changed
  // somewhere else on the page (the Process Sources modal). Shape:
  // `{ activeSkill, skillIntensity, nonce }` — a bumped nonce applies it once.
  // Nonce-gated rather than value-compared because props lag state by a
  // render: a plain comparison would see the pre-change prop right after a
  // toggle made HERE and revert it.
  skillSync = null,
}) {
  // Live Activity: the clinic assistant counts as "AI chat open" while mounted.
  useEffect(() => {
    beginActivity('clinic_ai_chat');
    return () => endActivity('clinic_ai_chat');
  }, []);

  const patientName = patientData?.patient_name || patientData?.name || 'Unknown';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // Persist selectedSources per-patient so toggles survive thread switches.
  const [selectedSources, setSelectedSources] = useState(() =>
    Object.fromEntries(ALL_SOURCES.map(s => [s.key, s.defaultOn])),
  );
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  // Map of localId → DOM node so the pinned bar can scroll directly to a
  // message even after re-renders. Re-populated by the ref callback below.
  const messageNodesRef = useRef(new Map());
  // localIds that should briefly flash a ring after a pinned-bar jump.
  const [flashId, setFlashId] = useState(null);
  // Tracks which patients we've already auto-fired the labs-summary query
  // for this session. Combined with the "history is empty" check below, the
  // auto-query fires only the very first time a patient is opened; on
  // subsequent visits history is non-empty so it stays quiet.
  const autoQueryFiredRef = useRef(new Set());
  // Held in a ref so the deferred auto-fire (setTimeout) calls the freshest
  // closure of `send` instead of the one captured when the effect first ran.
  const sendRef = useRef(null);
  // Same pattern for togglePin — the auto-fire's onComplete callback uses
  // this ref to pin the just-returned exchange.
  const togglePinRef = useRef(null);
  // Ids of pre-visit brief exchanges the server removed from this chat (a brief filed for a
  // patient the visit no longer holds): a history read that began before the delete still holds
  // them and must not bring them back.
  const removedBriefIdsRef = useRef(new Set());

  // ── Settings (ported from the doctor-page AIAssistant) ──────────────────────
  // Access is gated by SETTINGS_PASSWORD (not by role). The controls reuse the
  // SAME localStorage keys and the SAME query params as AIAssistant.js so the
  // clinic page behaves identically to the patient page. Note: chatModel mirrors
  // AIAssistant's guard — tenant/medstudent roles route through the agentic
  // service (chatModel sent as null), so the Chat Model picker persists the
  // preference but does not change clinic-query routing.
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsPasswordError, setSettingsPasswordError] = useState('');
  const [settingsTab, setSettingsTab] = useState('ocr'); // 'ocr' | 'chat'
  const [settingsNotice, setSettingsNotice] = useState(''); // inline "saved/reset" confirmation
  const settingsNoticeTimerRef = useRef(null);
  const saveCloseTimerRef = useRef(null);

  const loadSettingsFromStorage = () => {
    try {
      return {
        speedSetting: localStorage.getItem('aiAssistant_speedSetting') || 'fast',
        customMaxCharsPerDoc: localStorage.getItem('aiAssistant_customMaxCharsPerDoc')
          ? parseInt(localStorage.getItem('aiAssistant_customMaxCharsPerDoc'), 10)
          : 5000,
        customMaxTotalLength: localStorage.getItem('aiAssistant_customMaxTotalLength')
          ? parseInt(localStorage.getItem('aiAssistant_customMaxTotalLength'), 10)
          : 50000,
        isSettingsUnlocked: localStorage.getItem('aiAssistant_isSettingsUnlocked') === 'true',
        chatModel: localStorage.getItem('aiAssistant_chatModel')
          ? parseInt(localStorage.getItem('aiAssistant_chatModel'), 10)
          : 1,
        activeSkill: localStorage.getItem('aiAssistant_activeSkill') === 'caveman' ? 'caveman' : null,
        skillIntensity: CAVEMAN_INTENSITIES.includes(localStorage.getItem('aiAssistant_skillIntensity'))
          ? localStorage.getItem('aiAssistant_skillIntensity')
          : 'full',
      };
    } catch (e) {
      console.warn('Failed to load AI settings from localStorage:', e);
      return { speedSetting: 'fast', customMaxCharsPerDoc: 5000, customMaxTotalLength: 50000, isSettingsUnlocked: false, chatModel: 1, activeSkill: null, skillIntensity: 'full' };
    }
  };

  const initialSettings = loadSettingsFromStorage();
  const [speedSetting, setSpeedSetting] = useState(initialSettings.speedSetting);
  const [customMaxCharsPerDoc, setCustomMaxCharsPerDoc] = useState(initialSettings.customMaxCharsPerDoc);
  const [customMaxTotalLength, setCustomMaxTotalLength] = useState(initialSettings.customMaxTotalLength);
  const [isSettingsUnlocked, setIsSettingsUnlocked] = useState(initialSettings.isSettingsUnlocked);
  const [chatModel, setChatModel] = useState(initialSettings.chatModel);
  const [activeSkill, setActiveSkill] = useState(initialSettings.activeSkill);
  const [skillIntensity, setSkillIntensity] = useState(initialSettings.skillIntensity);
  // Set the moment the user touches the Skill controls, so the async DB-settings
  // load below can't stomp a choice made right after mount (see AIAssistant.js
  // for the same fix — that race was why a fresh toggle looked like it "didn't
  // take" until the second try).
  const skillTouchedRef = useRef(false);
  // "Caveman · full" (or null) — drives the header badge below.
  const activeSkillLabel = skillBadgeLabel(activeSkill, skillIntensity);

  const [availableModels, setAvailableModels] = useState([]);
  // Built-in chat models: the newest Claude models, fetched live from Anthropic
  // through the backend (plus what the legacy 0/1/2 choices map to).
  const [chatModelCatalog, setChatModelCatalog] = useState({ chatModels: [], legacyMap: null, error: null, loading: true });
  useEffect(() => {
    let cancelled = false;
    fetchChatModels(getBackendURL()).then((r) => {
      if (!cancelled) setChatModelCatalog({ chatModels: r.chatModels, legacyMap: r.legacyMap, error: r.ok ? null : r.error, loading: false });
    });
    return () => { cancelled = true; };
  }, []);
  const chatModelSelect = chatModelOptions(chatModelCatalog.chatModels, chatModel, chatModelCatalog.legacyMap);
  const [selectedModels, setSelectedModels] = useState(() => {
    try {
      const saved = localStorage.getItem('aiAssistant_selectedModels');
      if (saved) return JSON.parse(saved);
      const legacySaved = localStorage.getItem('aiAssistant_selectedModel');
      if (legacySaved) return [legacySaved, legacySaved, legacySaved];
      return DEFAULT_OCR_MODELS;
    } catch (e) {
      console.warn('Failed to parse saved models:', e);
      return DEFAULT_OCR_MODELS;
    }
  });
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelLoadError, setModelLoadError] = useState(null);
  // Default source selection (which source chips are pre-selected on load).
  // null = "no explicit choice" → fall back to ALL_SOURCES defaultOn.
  const [defaultSources, setDefaultSources] = useState(null);
  const [authUserId, setAuthUserId] = useState(null);
  const appliedDefaultsRef = useRef(false);
  const aiChatSettingsRevisionRef = useRef(0);

  // Persist each setting under the shared aiAssistant_* keys.
  useEffect(() => { try { localStorage.setItem('aiAssistant_speedSetting', speedSetting); } catch (_) {} }, [speedSetting]);
  useEffect(() => { try { localStorage.setItem('aiAssistant_customMaxCharsPerDoc', String(customMaxCharsPerDoc)); } catch (_) {} }, [customMaxCharsPerDoc]);
  useEffect(() => { try { localStorage.setItem('aiAssistant_customMaxTotalLength', String(customMaxTotalLength)); } catch (_) {} }, [customMaxTotalLength]);
  useEffect(() => { try { localStorage.setItem('aiAssistant_isSettingsUnlocked', String(isSettingsUnlocked)); } catch (_) {} }, [isSettingsUnlocked]);
  useEffect(() => { try { localStorage.setItem('aiAssistant_chatModel', String(chatModel)); } catch (_) {} }, [chatModel]);
  useEffect(() => { try { localStorage.setItem('aiAssistant_selectedModels', JSON.stringify(selectedModels)); } catch (_) {} }, [selectedModels]);
  useEffect(() => {
    try {
      if (activeSkill) localStorage.setItem('aiAssistant_activeSkill', activeSkill);
      else localStorage.removeItem('aiAssistant_activeSkill');
    } catch (_) {}
  }, [activeSkill]);
  useEffect(() => { try { localStorage.setItem('aiAssistant_skillIntensity', skillIntensity); } catch (_) {} }, [skillIntensity]);

  // Load per-user settings from the DB (users.ai_chat_settings) once on mount.
  // localStorage seeds the initial state synchronously above; the DB is the
  // source of truth and reconciles here. Degrades to localStorage/defaults if
  // there's no session or the column doesn't exist yet.
  useEffect(() => {
    let cancelled = false;
    const startedRevision = aiChatSettingsRevisionRef.current;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const uid = session?.user?.id;
        if (cancelled || !uid) return;
        setAuthUserId(uid);
        const loaded = await loadAiChatSettings(supabase, uid, {
          mirrorToLocal: false,
          backfillToDb: false,
        });
        if (cancelled || aiChatSettingsRevisionRef.current !== startedRevision) return;
        setSpeedSetting(loaded.speedSetting);
        setCustomMaxCharsPerDoc(loaded.customMaxCharsPerDoc);
        setCustomMaxTotalLength(loaded.customMaxTotalLength);
        setChatModel(loaded.chatModel);
        if (!skillTouchedRef.current) {
          setActiveSkill(loaded.activeSkill);
          setSkillIntensity(loaded.skillIntensity);
        }
        if (Array.isArray(loaded.ocrModels) && loaded.ocrModels.length) {
          setSelectedModels(loaded.ocrModels);
        }
        setDefaultSources(loaded.defaultSources);
      } catch (e) {
        // Non-fatal — localStorage/defaults remain in effect.
        if (!cancelled) console.warn('MedStudentAIAssistant: could not load ai_chat_settings', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply the saved default sources to the live selection once, after they load.
  // null = no explicit choice → keep the ALL_SOURCES defaultOn selection.
  useEffect(() => {
    if (appliedDefaultsRef.current || defaultSources == null) return;
    appliedDefaultsRef.current = true;
    setSelectedSources(
      Object.fromEntries(ALL_SOURCES.map((s) => [s.key, defaultSources.includes(s.key)])),
    );
  }, [defaultSources]);

  // Fetch OCR models from the AgenticAI backend (same endpoint AIAssistant uses).
  const fetchAvailableModels = useCallback(async () => {
    try {
      setIsLoadingModels(true);
      setModelLoadError(null);
      const backendBaseUrl = (() => {
        let injectedUrl = window.ENV?.VERCEL_OCR_URL;
        if (injectedUrl && (injectedUrl.includes('{{') || injectedUrl === '')) injectedUrl = null;
        const override =
          injectedUrl ||
          process.env.REACT_APP_VERCEL_OCR_URL ||
          process.env.VERCEL_OCR_URL ||
          process.env.REACT_APP_AGENTICAI_URL ||
          process.env.AGENTICAI_BASE_URL;
        const hostname = window.location.hostname;
        const url = override && override.trim() !== ''
          ? override
          : hostname === 'localhost' || hostname === '127.0.0.1'
          ? BASE_URLS.local.agenticai
          : BASE_URLS.render.agenticai;
        return url.trim().replace(/\/+$/, '');
      })();
      const response = await fetch(`${backendBaseUrl}/api/models`);
      if (!response.ok) throw new Error(`Failed to fetch models: ${response.status}`);
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        throw new Error(`Invalid response format from server (expected JSON, got ${contentType})`);
      }
      const data = await response.json();
      const models = data.models || [];
      setAvailableModels(models);
      if (models.length > 0) {
        // Fill empty priority slots with the newest model of each provider.
        const defaults = defaultOcrModelsFrom(models);
        setSelectedModels((prev) => {
          const newModels = [...prev];
          let changed = false;
          [0, 1, 2].forEach((i) => { if (!newModels[i]) { newModels[i] = defaults[i] || defaults[0]; changed = true; } });
          if (changed) {
            try { localStorage.setItem('aiAssistant_selectedModels', JSON.stringify(newModels)); } catch (_) {}
            return newModels;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching models from AgenticAI:', error);
      setModelLoadError(error.message);
      // No hardcoded stand-ins: the error is shown in the settings panel.
      setAvailableModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  useEffect(() => { fetchAvailableModels(); }, [fetchAvailableModels]);

  // Processing-speed → document-truncation params (same mapping as AIAssistant).
  const getTruncationParameters = useCallback(() => {
    switch (speedSetting) {
      case 'fast': return { maxCharsPerDocument: 5000, maxTotalContentLength: 50000 };
      case 'medium': return { maxCharsPerDocument: 10000, maxTotalContentLength: 100000 };
      case 'slow': return { maxCharsPerDocument: 1000000, maxTotalContentLength: 1000000 };
      case 'advanced': return { maxCharsPerDocument: customMaxCharsPerDoc, maxTotalContentLength: customMaxTotalLength };
      default: return { maxCharsPerDocument: 5000, maxTotalContentLength: 50000 };
    }
  }, [speedSetting, customMaxCharsPerDoc, customMaxTotalLength]);

  const handleSettingsPasswordSubmit = () => {
    if (settingsPassword === SETTINGS_PASSWORD) {
      setIsSettingsUnlocked(true);
      setSettingsPasswordError('');
    } else {
      setSettingsPasswordError('Incorrect password');
      setSettingsPassword('');
    }
  };

  // Brief inline confirmation pill (the clinic page uses inline status, not toasts).
  const showSettingsNotice = (msg) => {
    setSettingsNotice(msg);
    if (settingsNoticeTimerRef.current) clearTimeout(settingsNoticeTimerRef.current);
    settingsNoticeTimerRef.current = setTimeout(() => setSettingsNotice(''), 2500);
  };

  const openSettings = () => {
    setIsSettingsOpen(true);
    setIsSettingsUnlocked(false); // re-lock on every open (matches AIAssistant)
    setSettingsPassword('');
    setSettingsPasswordError('');
    setSettingsNotice('');
    setSettingsTab('ocr');
  };

  // Same opener, but landing on the Skills tab — used by the header badge,
  // whose whole point is "this is on; take me to where I turn it off".
  const openSkillSettings = () => {
    openSettings();
    setSettingsTab('skills');
  };

  const closeSettings = () => {
    setIsSettingsOpen(false);
    setIsSettingsUnlocked(false);
    setSettingsPassword('');
    setSettingsPasswordError('');
  };

  const resetOcrModels = () => {
    const defaults = defaultOcrModelsFrom(availableModels);
    if (!defaults.length) {
      showSettingsNotice('The live model list is unavailable, so there is nothing to reset to.');
      return;
    }
    setSelectedModels(defaults);
    const names = defaults
      .map((id) => availableModels.find((m) => m.model_id === id)?.config?.display_name || id)
      .join(', ');
    showSettingsNotice(`OCR models reset to the newest of each provider: ${names}`);
  };

  // Default Sources tab — null means "use ALL_SOURCES defaults"; the first edit
  // makes the choice explicit (an array, even empty).
  const effectiveDefaultSources =
    defaultSources != null
      ? defaultSources
      : ALL_SOURCES.filter((s) => s.defaultOn).map((s) => s.key);
  const toggleDefaultSource = (key) => {
    setDefaultSources((prev) => {
      const base = prev != null ? prev : ALL_SOURCES.filter((s) => s.defaultOn).map((s) => s.key);
      return base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    });
  };

  // Persist to the DB (users.ai_chat_settings) + localStorage cache, then close.
  const saveSettings = async () => {
    const settingsToSave = normalizeAiChatSettings({
      speedSetting,
      customMaxCharsPerDoc,
      customMaxTotalLength,
      chatModel,
      ocrModels: selectedModels,
      defaultSources,
      activeSkill,
      skillIntensity,
    });
    aiChatSettingsRevisionRef.current += 1;
    if (typeof onSettingsSaved === 'function') {
      onSettingsSaved(settingsToSave);
    }
    const supabase = await getSupabase();
    const res = await saveAiChatSettings(supabase, authUserId, settingsToSave);
    showSettingsNotice(res.ok ? 'Settings saved' : 'Saved on this device (sync failed)');
    if (saveCloseTimerRef.current) clearTimeout(saveCloseTimerRef.current);
    saveCloseTimerRef.current = setTimeout(() => setIsSettingsOpen(false), 700);
  };

  // ── Skill auto-persist ──────────────────────────────────────────────────
  // The Skill choice is a "set it once and it follows me" preference, so it
  // must not depend on the user ALSO remembering to press Save Settings, and
  // it must not live only in this device's localStorage. Picking a skill
  // writes it straight to users.ai_chat_settings.
  //   - skillTouchedRef gates it to real user edits, so the mount-time DB load
  //     never echoes what it just read back to the server.
  //   - the signature ref means a change to any OTHER setting (which re-runs
  //     this effect through its deps) doesn't trigger a redundant write.
  const lastSkillSaveRef = useRef(null);
  useEffect(() => {
    if (!skillTouchedRef.current || !authUserId) return;
    const signature = `${activeSkill || 'none'}:${skillIntensity}`;
    if (lastSkillSaveRef.current === signature) return;
    lastSkillSaveRef.current = signature;
    const settingsToSave = normalizeAiChatSettings({
      speedSetting,
      customMaxCharsPerDoc,
      customMaxTotalLength,
      chatModel,
      ocrModels: selectedModels,
      defaultSources,
      activeSkill,
      skillIntensity,
    });
    // Same revision bump + parent notify the explicit Save does, so an
    // in-flight settings hydrate can't stomp the choice and the clinic page
    // (Process Sources modal, Master Note) sees the new skill immediately.
    aiChatSettingsRevisionRef.current += 1;
    if (typeof onSettingsSaved === 'function') onSettingsSaved(settingsToSave);
    (async () => {
      try {
        const supabase = await getSupabase();
        await saveAiChatSettings(supabase, authUserId, settingsToSave);
      } catch (_) { /* localStorage mirror already written — non-fatal */ }
    })();
  }, [
    activeSkill, skillIntensity, authUserId, onSettingsSaved,
    speedSetting, customMaxCharsPerDoc, customMaxTotalLength,
    chatModel, selectedModels, defaultSources,
  ]);

  // The Process Sources modal edits the SAME preference and persists it
  // itself. Mirror the change in so the header badge and the next query agree
  // with what the user just picked, instead of lagging until a remount.
  // Pre-seeding lastSkillSaveRef marks the value as already-written so the
  // auto-persist effect above doesn't bounce it back to the server; setting
  // skillTouchedRef keeps a late mount-time hydrate from stomping it.
  const lastSkillSyncNonceRef = useRef(null);
  useEffect(() => {
    if (!skillSync || skillSync.nonce == null) return;
    if (lastSkillSyncNonceRef.current === skillSync.nonce) return;
    lastSkillSyncNonceRef.current = skillSync.nonce;
    const nextSkill = skillSync.activeSkill === 'caveman' ? 'caveman' : null;
    const nextIntensity = CAVEMAN_INTENSITIES.includes(skillSync.skillIntensity)
      ? skillSync.skillIntensity
      : 'full';
    lastSkillSaveRef.current = `${nextSkill || 'none'}:${nextIntensity}`;
    skillTouchedRef.current = true;
    setActiveSkill(nextSkill);
    setSkillIntensity(nextIntensity);
  }, [skillSync]);

  // Clear pending timers on unmount.
  useEffect(() => () => {
    if (settingsNoticeTimerRef.current) clearTimeout(settingsNoticeTimerRef.current);
    if (saveCloseTimerRef.current) clearTimeout(saveCloseTimerRef.current);
  }, []);

  // Thread state. The backend stores chat history in `ai_feedback` keyed by
  // (patient_id, thread_id, username); thread_id is an integer (1, 2, …),
  // matching the doctor AIAssistant pattern so the same backend endpoints
  // serve both UIs. `threads` is the list of known threads for this patient
  // (loaded via /api/ai-assistant/threads/ + any new ones created locally).
  // `activeThreadId` is the one whose history the panel currently shows.
  const [activeThreadId, setActiveThreadId] = useState(1);
  const [threads, setThreads] = useState([{ thread_id: 1 }]);
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current && patientId) {
    sessionIdRef.current = `medstudent_${patientId}_${Date.now()}`;
  }

  // Discover existing threads for this patient on mount / patient-switch so
  // the tab bar shows everything the user has ever opened — not just the
  // default thread 1. New threads created locally (via the "+" button) are
  // merged in by their own setter below.
  useEffect(() => {
    if (!patientId) {
      setThreads([{ thread_id: 1 }]);
      setActiveThreadId(1);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        const userJwtToken = session?.access_token;
        if (!userJwtToken) return;
        const res = await fetch(`${getBackendURL()}/api/ai-assistant/threads/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userJwtToken}` },
          body: JSON.stringify({ patientId, schema }),
        });
        if (!res.ok) return;
        const data = await res.json();
        const dbThreads = Array.isArray(data.threads) ? data.threads : [];
        if (cancelled) return;
        // Always include thread 1 (the auto-fire target) even if the DB
        // returned none — there's no thread until the first message lands.
        const ids = new Set([1, ...dbThreads.map((t) => t.thread_id).filter(Boolean)]);
        const merged = Array.from(ids)
          .sort((a, b) => a - b)
          .map((id) => {
            const fromDb = dbThreads.find((t) => t.thread_id === id);
            return fromDb || { thread_id: id };
          });
        setThreads(merged);
        // Switch to the highest existing thread by default — the user usually
        // wants to land on their most recent conversation.
        const lastId = merged[merged.length - 1]?.thread_id ?? 1;
        setActiveThreadId(lastId);
      } catch (_) { /* keep default thread 1 */ }
    })();
    return () => { cancelled = true; };
  }, [patientId, schema]);

  // Create a new local thread (next integer after the current max) and
  // switch to it. The thread doesn't exist in the DB until the user's first
  // message in it is auto-saved by the backend's /query/ endpoint.
  const createNewThread = useCallback(() => {
    if (!patientId) return;
    setThreads((prev) => {
      const maxId = prev.reduce((m, t) => Math.max(m, t.thread_id || 0), 0);
      const nextId = maxId + 1;
      setActiveThreadId(nextId);
      return [...prev, { thread_id: nextId }];
    });
  }, [patientId]);

  // Load any persisted history for this patient/thread so users see prior turns
  // when they reopen the clinic page. Mirrors doctor AIAssistant's chat-history
  // load on thread switch (POST /api/ai-assistant/chat-history/).
  useEffect(() => {
    if (!patientId) {
      setMessages([]);
      sessionIdRef.current = null;
      return;
    }
    sessionIdRef.current = `medstudent_${patientId}_${Date.now()}`;
    let cancelled = false;
    (async () => {
      // Optimistic greeting while we load history.
      setMessages([{ role: 'assistant', text: defaultGreeting(patientName), time: nowHHmm(), localId: makeLocalId() }]);
      try {
        const sb = await getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        const userJwtToken = session?.access_token;
        if (!userJwtToken) return;
        const res = await fetch(`${getBackendURL()}/api/ai-assistant/chat-history/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userJwtToken}` },
          body: JSON.stringify({
            patientId,
            threadId: activeThreadId,
            schema,
          }),
        });
        if (!res.ok) return;
        const data = await res.json();
        // Backend returns one entry per message with `type` ('user' | 'assistant')
        // and `content`. `feedback_id` + `pinned` come from the underlying
        // ai_feedback row and are SHARED between the user prompt and assistant
        // response (one DB row per exchange).
        const history = Array.isArray(data.messages) ? data.messages : [];
        const flat = history
          .filter(m => m.content)
          .map(m => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            text: m.content,
            time: m.timestamp
              ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : nowHHmm(),
            feedbackId: m.feedback_id ?? null,
            pinned: !!m.pinned,
            status: m.status ?? null,
            localId: makeLocalId(),
          }));
        if (!cancelled && flat.length > 0) {
          const greeting = { role: 'assistant', text: defaultGreeting(patientName), time: nowHHmm(), localId: makeLocalId() };
          // A pre-visit brief filed while this was loading can be missing from what the
          // server returned; keepAttachedBriefs carries it over instead of wiping it.
          setMessages((prev) => [greeting, ...keepAttachedBriefs(flat, prev, removedBriefIdsRef.current)]);
        }
        // Auto-fire the labs-summary query on every page load IF:
        //   (a) we're on thread 1 (the auto-summary is a per-patient "first
        //       contact" overview, not a per-thread habit), AND
        //   (b) the patient has reports or submitted forms (otherwise there's
        //       nothing for the AI to summarize), AND
        //   (c) thread 1's history doesn't already contain the same lab-
        //       summary question (we never want to ask it twice).
        // When fired, the response is auto-pinned so it stays in the pinned
        // bar across sessions. autoQueryFiredRef is still useful as a
        // same-session guard against firing twice before the first save
        // lands in the DB (the history check would otherwise re-pass).
        const alreadyHasLabQuery = flat.some(
          (m) => m.role === 'user' && LAB_SUMMARY_PROMPT_PATTERN.test(m.text || ''),
        );
        const autoFireKey = `${patientId}::${activeThreadId}`;
        if (!cancelled && activeThreadId === 1 && !alreadyHasLabQuery && !autoQueryFiredRef.current.has(autoFireKey)) {
          // Check sources of patient data in priority order — forms is a
          // cheap array read on patientData; reports needs a storage list.
          const hasForms = patientHasForms(patientData);
          const hasReports = hasForms ? true : await patientHasReports(patientData);
          if (!cancelled && (hasForms || hasReports)) {
            autoQueryFiredRef.current.add(autoFireKey);
            const sex = String(patientData?.sex || patientData?.gender || '').toLowerCase();
            const pronounPossessive = (sex === 'm' || sex === 'male') ? 'his'
                                    : (sex === 'f' || sex === 'female') ? 'her'
                                    : "the patient's";
            const pronounContext = (sex === 'm' || sex === 'male' || sex === 'f' || sex === 'female')
              ? pronounPossessive
              : "the patient's";
            const labQuery =
              `What were ${pronounPossessive} most recent lab results? Any flags? ` +
              `Summarize the most important findings from ${pronounContext} uploaded reports and submitted forms in a markdown table with columns: Item, Value, Reference / Normal, Status. ` +
              `If the reports don't contain traditional lab panels, extract whatever clinical data IS present — vital signs, diagnostic impressions, exam findings, imaging results, medications, key history points — and put those in the table instead of refusing. ` +
              `Use 🚨 for critical/abnormal, ⚠️ for borderline, ✓ for normal — judging against ${pronounContext} age and demographics. Always produce the table from the available content; do not respond with a generic "no data available" message when reports exist.`;
            // Defer one tick so the greeting renders first.
            setTimeout(() => {
              if (cancelled) return;
              // Pull from every source for this summary — Reports
              // (uploaded_files) is off by default but is critical for lab data.
              // The onComplete callback flips the exchange's pinned flag
              // once the backend stamps a feedback_id onto the response.
              sendRef.current?.(
                labQuery,
                ALL_SOURCES.map((s) => s.key),
                (feedbackId) => {
                  if (feedbackId != null) togglePinRef.current?.(feedbackId, true);
                },
              );
            }, 250);
          }
        }
      } catch (_) { /* leave greeting */ }
    })();
    return () => { cancelled = true; };
  // patientData is in the deps because the prompt's pronoun depends on it;
  // the effect itself is keyed on patientId + activeThreadId so it re-runs
  // on thread switches and patient switches but not on unrelated patientData
  // updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, patientName, activeThreadId, schema]);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Cancel inflight request on unmount
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // ── Pin toggle ────────────────────────────────────────────────────────────
  // Toggling pins writes directly to `ai_feedback.pinned` via the active
  // supabase client. RLS lets the row's owner (matched on username) update
  // their own row; the migration intentionally didn't add a new policy.
  const togglePin = useCallback(async (feedbackId, nextPinned) => {
    if (feedbackId == null) return;
    // Optimistic update — flip both messages backed by this feedback_id.
    setMessages(prev => prev.map(m =>
      m.feedbackId === feedbackId ? { ...m, pinned: nextPinned } : m
    ));
    try {
      const sb = await getSupabase();
      const { error } = await sb
        .schema(schema)
        .from('ai_feedback')
        .update({ pinned: nextPinned })
        .eq('id', feedbackId);
      if (error) throw error;
    } catch (err) {
      console.warn('[MedStudentAIAssistant] pin toggle failed:', err.message);
      // Revert on failure.
      setMessages(prev => prev.map(m =>
        m.feedbackId === feedbackId ? { ...m, pinned: !nextPinned } : m
      ));
    }
  }, [schema]);

  // The pre-visit brief strip asks the server to file the visit's brief as a pinned exchange
  // (and to delete any brief filed for another patient): show the answer here without a reload,
  // the new row, the pin off any earlier brief, and the removed exchanges gone.
  const handleBriefAttached = useCallback((result) => {
    noteRemovedBriefs(removedBriefIdsRef.current, result);
    setMessages((prev) => withAttachedBrief(prev, result, makeLocalId));
  }, []);

  // ── Helpful/not-helpful feedback ────────────────────────────────────────
  // Rendered at the END of each assistant reply (not the top-of-bubble
  // header, which scrolls out of view above long responses). Same
  // Yes/No-then-optional-comment-then-Submit pattern already used for
  // Master Note feedback (ProcessModal.js) and the general AI chat
  // (AIAssistant.js) — draft state here is local/pre-submit; `m.status`
  // only reflects what's actually been written to the DB.
  const [feedbackDrafts, setFeedbackDrafts] = useState({}); // { [feedbackId]: { status, text, submitting, submitted, error } }

  const chooseFeedbackStatus = useCallback((feedbackId, status) => {
    setFeedbackDrafts(prev => ({
      ...prev,
      [feedbackId]: { ...(prev[feedbackId] || { text: '' }), status, submitted: false, error: null },
    }));
  }, []);

  const cancelFeedbackDraft = useCallback((feedbackId) => {
    setFeedbackDrafts(prev => {
      const next = { ...prev };
      delete next[feedbackId];
      return next;
    });
  }, []);

  const setFeedbackDraftText = useCallback((feedbackId, text) => {
    setFeedbackDrafts(prev => ({
      ...prev,
      [feedbackId]: { ...(prev[feedbackId] || { status: null }), text },
    }));
  }, []);

  const submitFeedback = useCallback(async (feedbackId) => {
    const draft = feedbackDrafts[feedbackId];
    if (feedbackId == null || !draft?.status) return;
    setFeedbackDrafts(prev => ({ ...prev, [feedbackId]: { ...prev[feedbackId], submitting: true, error: null } }));
    try {
      const sb = await getSupabase();
      const { error } = await sb
        .schema(schema)
        .from('ai_feedback')
        .update({ status: draft.status, feedback_text: draft.text || null })
        .eq('id', feedbackId);
      if (error) throw error;
      setMessages(prev => prev.map(m =>
        m.feedbackId === feedbackId ? { ...m, status: draft.status } : m
      ));
      setFeedbackDrafts(prev => ({ ...prev, [feedbackId]: { ...prev[feedbackId], submitting: false, submitted: true } }));
    } catch (err) {
      console.warn('[MedStudentAIAssistant] feedback submit failed:', err.message);
      setFeedbackDrafts(prev => ({ ...prev, [feedbackId]: { ...prev[feedbackId], submitting: false, error: err.message || 'Failed to submit feedback.' } }));
    }
  }, [schema, feedbackDrafts]);

  // Mirror togglePin into a ref so the auto-fired query's onComplete callback
  // (declared inside the history-load effect, which runs before togglePin is
  // defined in source order) can pin the response without TDZ issues.
  useEffect(() => { togglePinRef.current = togglePin; }, [togglePin]);

  // Pinned bar item click → scroll to the user-prompt half of the pair so the
  // question (which is what the bar previews) lands in view.
  const scrollToFeedback = useCallback((feedbackId) => {
    const userMsg = messages.find(m => m.feedbackId === feedbackId && m.role === 'user')
      || messages.find(m => m.feedbackId === feedbackId);
    if (!userMsg) return;
    const node = messageNodesRef.current.get(userMsg.localId);
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(userMsg.localId);
    setTimeout(() => setFlashId(id => (id === userMsg.localId ? null : id)), 1500);
  }, [messages]);

  // Distinct pinned exchanges (one entry per feedback_id, taking the prompt
  // text as the preview because that's the user's question).
  const pinnedItems = (() => {
    const seen = new Map();
    for (const m of messages) {
      if (!m.pinned || m.feedbackId == null) continue;
      if (!seen.has(m.feedbackId)) {
        seen.set(m.feedbackId, { feedbackId: m.feedbackId, prompt: null, time: m.time });
      }
      const entry = seen.get(m.feedbackId);
      if (m.role === 'user' && !entry.prompt) entry.prompt = m.text;
    }
    // Fallback: if for some reason we only have the assistant message, use its
    // text snippet so the bar still shows something useful.
    for (const m of messages) {
      if (!m.pinned || m.feedbackId == null) continue;
      const entry = seen.get(m.feedbackId);
      if (entry && !entry.prompt) entry.prompt = m.text;
    }
    return Array.from(seen.values());
  })();

  const send = useCallback(
    async (text, sourcesOverride, onComplete) => {
      const q = (text || '').trim();
      if (!q || loading) return;
      const userLocalId = makeLocalId();
      setMessages(m => [...m, { role: 'user', text: q, time: nowHHmm(), localId: userLocalId }]);
      setInput('');
      setLoading(true);
      try {
        // sourcesOverride lets the auto-fired "lab summary" query pull from
        // ALL sources (especially uploaded_files / Reports) regardless of
        // the user's chip toggles — the goal is the broadest first picture.
        const hasForms = patientHasForms(patientData);
        const enabledSources = Array.isArray(sourcesOverride)
          ? sourcesOverride
          : Object.entries(selectedSources)
              .filter(([, on]) => on)
              .map(([k]) => k)
              // Drop a stale 'forms' selection carried over from a patient
              // who had forms — the backend would otherwise spend a query
              // pass looking for submissions that don't exist here.
              .filter((k) => k !== 'forms' || hasForms);

        const plainFromLiveRef =
          documentPlainRef?.current != null && documentPlainRef.current !== ''
            ? documentPlainRef.current
            : null;
        const resolvedDocumentContent =
          plainFromLiveRef != null ? plainFromLiveRef : documentContent;

        // The session's access token goes in the Authorization header; the
        // backend verifies it and takes the user id / username from it.
        let userJwtToken = null;
        try {
          const sb = await getSupabase();
          const { data: { session } } = await sb.auth.getSession();
          userJwtToken = session?.access_token ?? null;
        } catch (_) {}

        // Settings-driven params (same as AIAssistant): document truncation from
        // the Processing Speed setting, plus OCR model priority. chatModel mirrors
        // AIAssistant's guard — tenant/medstudent route through the agentic service
        // (null), so it never changes clinic-query routing.
        const truncationParams = getTruncationParameters();

        const controller = new AbortController();
        abortRef.current = controller;
        const res = await fetch(`${getBackendURL()}/api/ai-assistant/query/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(userJwtToken ? { Authorization: `Bearer ${userJwtToken}` } : {}),
          },
          signal: controller.signal,
          body: JSON.stringify({
            query: q,
            patientId,
            // Always pass `patientData` — the backend doesn't only use it for
            // the LLM prompt, it also needs `patient_name` from this object
            // to query `notes-c` when `visit_history` is selected. Gating
            // patientData on patient_info caused "Prior visits?" to return
            // "no previous visits found" because the visit-history fetch
            // had nothing to filter by. The bulky context the gate was
            // trying to suppress lives in `documentContent`, which is still
            // gated below.
            patientData,
            patientName,
            documentContent: enabledSources.includes('medical_document') ? (resolvedDocumentContent || null) : null,
            sessionId: sessionIdRef.current,
            // Integer thread id — matches the doctor AIAssistant pattern so the
            // backend can find prior turns saved under the same (patient,thread).
            threadId: activeThreadId,
            selectedSources: enabledSources,
            selectedReportFiles: enabledSources.includes('uploaded_files') ? null : [],
            schema,
            userRole: userRole || 'medstudent',
            scheduledDate: scheduledDate || null,
            maxCharsPerDocument: truncationParams.maxCharsPerDocument,
            maxTotalContentLength: truncationParams.maxTotalContentLength,
            modelId: selectedModels[0], // OCR backward compatibility
            modelIds: selectedModels, // OCR priority list
            chatModel: isInGroup(userRole || 'medstudent', 'tenantSchema') ? null : chatModel,
            activeSkill,
            skillIntensity,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Doctor AIAssistant reads `data.answer`. Older shapes may use `response`/`text`.
        const answer = data.answer || data.response || data.text || data.message || 'No response.';
        const sources = Array.isArray(data.sources) ? data.sources : [];
        // Backend stamps `feedback_id` onto the response after auto-save so we
        // can offer pin/unpin immediately, without waiting for the next
        // chat-history refresh.
        const feedbackId = data.feedback_id ?? null;
        setMessages(m => {
          // Back-fill the user message we just sent so it shares the same
          // feedback_id (one ai_feedback row per exchange = both bubbles toggle
          // pin together).
          const withUser = m.map(msg =>
            msg.localId === userLocalId ? { ...msg, feedbackId, pinned: false, status: null } : msg
          );
          return [
            ...withUser,
            {
              role: 'assistant',
              text: answer,
              time: nowHHmm(),
              sources,
              feedbackId,
              pinned: false,
              status: null,
              localId: makeLocalId(),
              activeSkill: data.activeSkill || null,
              skillIntensity: data.skillIntensity || null,
            },
          ];
        });
        // Notify caller — used by the auto-fired labs-summary query to flip
        // its exchange to pinned as soon as the feedback_id is known.
        if (typeof onComplete === 'function') {
          try { onComplete(feedbackId); } catch (_) { /* non-fatal */ }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setMessages(m => [
          ...m,
          { role: 'assistant', text: `Sorry — there was an error: ${err.message}`, error: true, time: nowHHmm(), localId: makeLocalId() },
        ]);
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    },
    // activeSkill/skillIntensity MUST be listed: without them this closure kept
    // whatever skill was selected when it was last rebuilt, so the first query
    // after switching the skill went out with the OLD value. `loading` flips on
    // every send, which rebuilt the closure mid-request — that is exactly why
    // the new skill only "took" from the second query onward.
    [loading, selectedSources, patientId, patientData, patientName, documentContent, documentPlainRef, userRole, scheduledDate, activeThreadId, getTruncationParameters, selectedModels, chatModel, schema, activeSkill, skillIntensity],
  );

  // Keep sendRef pointing at the current send closure so the deferred
  // auto-query inside the history-load effect always calls the freshest one.
  // Declared AFTER `send` to avoid a TDZ ReferenceError at component init.
  useEffect(() => { sendRef.current = send; }, [send]);

  // Parent-injected prompt (Process modal, template-OFF path). Fires once per nonce.
  const lastInjectNonceRef = useRef(null);
  useEffect(() => {
    if (!injectPrompt || injectPrompt.nonce == null) return;
    if (lastInjectNonceRef.current === injectPrompt.nonce) return;
    lastInjectNonceRef.current = injectPrompt.nonce;
    if (Array.isArray(injectPrompt.sources)) {
      setSelectedSources((prev) => {
        const next = { ...prev };
        for (const k of injectPrompt.sources) next[k] = true;
        return next;
      });
    }
    sendRef.current?.(injectPrompt.text, injectPrompt.sources || undefined);
  }, [injectPrompt]);

  // Ref callback shared by every message div — populates a Map keyed by
  // localId so scrollToFeedback can jump to the right node.
  const registerNode = (localId) => (node) => {
    if (node) messageNodesRef.current.set(localId, node);
    else messageNodesRef.current.delete(localId);
  };

  return (
    <div
      className="flex-1 flex flex-col bg-white dark:bg-[#0a0a0f]"
      style={{ minHeight: 0 }}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800/60">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 flex items-center justify-center text-white shrink-0 shadow-md shadow-blue-500/30">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm6 10l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3zM5 14l.8 2.4L8 17l-2.2.6L5 20l-.8-2.4L2 17l2.2-.6L5 14z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-bold text-slate-900 dark:text-slate-100">AI Assistant</div>
              <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                LIVE
              </span>
              {/* Active-skill badge — the skill silently rewrites every reply,
                  so the chat says so up front (and which intensity), instead of
                  the setting being invisible until you open Settings. */}
              {activeSkillLabel && (
                <button
                  type="button"
                  data-testid="chat-skill-badge"
                  onClick={openSkillSettings}
                  title="Skill active — click to change it in Settings"
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-500/25"
                >
                  <span aria-hidden>🪨</span>
                  {activeSkillLabel}
                </button>
              )}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Active Patient ·{' '}
              <span className="font-semibold text-slate-700 dark:text-slate-200">{patientName}</span>
            </div>
          </div>
          {/* Settings — password-gated; opens the AI Assistant settings modal. */}
          <button
            type="button"
            onClick={openSettings}
            title="Settings"
            aria-label="Settings"
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
        {/* Thread tabs — one chip per known thread for this patient, plus
            a "+" to start a new conversation. The auto-fired labs-summary
            (the pinned overview) only ever fires on thread 1, so secondary
            threads are clean slates for the user's own questions. */}
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold tracking-widest text-slate-500 dark:text-slate-400 mr-1">
            THREADS
          </span>
          {threads.map((t) => {
            const isActive = t.thread_id === activeThreadId;
            return (
              <button
                key={t.thread_id}
                type="button"
                aria-pressed={isActive}
                onClick={() => setActiveThreadId(t.thread_id)}
                title={t.first_message_preview || `Thread ${t.thread_id}`}
                className={[
                  'text-[11px] font-semibold w-7 h-7 rounded-lg flex items-center justify-center',
                  isActive
                    ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-500/40'
                    : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700/60',
                ].join(' ')}
              >
                {t.thread_id}
              </button>
            );
          })}
          <button
            type="button"
            onClick={createNewThread}
            title="New thread"
            aria-label="New thread"
            className="text-[14px] leading-none w-7 h-7 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 hover:bg-blue-100 dark:hover:bg-blue-500/15 hover:text-blue-700 dark:hover:text-blue-300"
          >
            +
          </button>
        </div>
        {/* Source chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {ALL_SOURCES.map(src => {
            const on = !!selectedSources[src.key];
            // Forms is only meaningful when the patient has submitted forms —
            // disable + mute the chip (and never report it as "on") otherwise,
            // matching the doctor AIAssistant's hasFormsData gate.
            const disabled = src.requiresFormsData && !patientHasForms(patientData);
            return (
              <button
                key={src.key}
                type="button"
                aria-pressed={on && !disabled}
                disabled={disabled}
                title={disabled ? 'No submitted forms for this patient' : undefined}
                onClick={() => setSelectedSources(s => ({ ...s, [src.key]: !s[src.key] }))}
                className={[
                  'text-[11px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5',
                  disabled
                    ? 'bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-600 opacity-50 cursor-not-allowed'
                    : on
                      ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300'
                      : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400',
                ].join(' ')}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${on && !disabled ? 'bg-blue-500' : 'bg-slate-400'}`} />
                {src.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Pre-visit brief status — only while there is no brief in the chat yet, and only on
          the first thread, where the brief is filed. */}
      {activeThreadId === 1 && (
        <VisitBriefChatStrip
          schema={schema}
          patient={patientData}
          chatPatientId={patientId}
          onAttached={handleBriefAttached}
        />
      )}

      {/* Pinned-prompts bar — slim strip above the conversation. One row per
          pinned exchange; click jumps the chat to that prompt. The 📌 prefix
          + the pin button on the bubble are the two ways to manage pins. */}
      {pinnedItems.length > 0 && (
        <div className="pinned-bar border-b border-amber-200 dark:border-amber-500/25 bg-amber-50/70 dark:bg-amber-500/[0.06] divide-y divide-amber-100 dark:divide-amber-500/15">
          {pinnedItems.map(item => (
            <div key={item.feedbackId} className="flex items-center gap-2 px-3 py-1.5">
              <span className="text-xl leading-none shrink-0" aria-hidden>📌</span>
              <button
                type="button"
                onClick={() => scrollToFeedback(item.feedbackId)}
                title="Jump to pinned message"
                className="flex-1 min-w-0 text-left text-[11px] text-amber-900 dark:text-amber-100 truncate hover:underline"
              >
                {firstLine(item.prompt) || '(empty prompt)'}
              </button>
              <button
                type="button"
                onClick={() => togglePin(item.feedbackId, false)}
                aria-label="Unpin"
                title="Unpin"
                className="text-[10px] shrink-0 text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 px-1"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
        {messages.map((m) => {
          const isFlashing = flashId === m.localId;
          const canPin = m.feedbackId != null;
          const isPinned = !!m.pinned;
          // Wrapper around each message so we can attach a ref + flash ring.
          const wrapper = (children) => (
            <div
              key={m.localId}
              ref={registerNode(m.localId)}
              className={[
                'rounded-lg transition-shadow',
                isFlashing ? 'ring-2 ring-amber-400 dark:ring-amber-300 ring-offset-2 ring-offset-white dark:ring-offset-[#0a0a0f]' : '',
              ].join(' ')}
            >
              {children}
            </div>
          );
          if (m.role === 'assistant') {
            return wrapper(
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z" />
                    </svg>
                  </div>
                  <div className="text-[10px] font-bold tracking-widest text-slate-500 dark:text-slate-400">
                    ASSISTANT
                  </div>
                  {/* Copy the RENDERED answer (innerText + innerHTML) — selecting
                      chat text by hand fails when the panel re-renders mid-copy,
                      so give a one-click path into the note (Leejoe 2026-07-08:
                      "can't copy paste from AI chat to put things in note"). */}
                  <button
                    type="button"
                    onClick={(e) => {
                      const btn = e.currentTarget;
                      const holder = btn.parentElement && btn.parentElement.parentElement;
                      const bubble = holder ? holder.querySelector('.ai-markdown') : null;
                      const plain = (bubble ? bubble.innerText : (m.content || '')) || '';
                      const html = bubble ? bubble.innerHTML : '';
                      const done = () => {
                        btn.textContent = '✓ Copied';
                        setTimeout(() => { try { btn.textContent = '⧉ Copy'; } catch (_) {} }, 1200);
                      };
                      const plainOnly = () => navigator.clipboard.writeText(plain).then(done).catch(() => {});
                      try {
                        if (html && navigator.clipboard && window.ClipboardItem) {
                          navigator.clipboard.write([new window.ClipboardItem({
                            'text/plain': new Blob([plain], { type: 'text/plain' }),
                            'text/html': new Blob([html], { type: 'text/html' }),
                          })]).then(done).catch(plainOnly);
                        } else {
                          plainOnly();
                        }
                      } catch (_) { plainOnly(); }
                    }}
                    title="Copy this answer — then paste into the note or anywhere else"
                    aria-label="Copy this answer"
                    className="ml-auto shrink-0 select-none rounded-md px-2 py-1 text-[10px] font-semibold border text-slate-500 dark:text-slate-400 bg-slate-100/60 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/50 hover:text-blue-600 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-500/40 hover:bg-blue-50 dark:hover:bg-blue-500/10"
                  >
                    ⧉ Copy
                  </button>
                  {canPin && (
                    <button
                      type="button"
                      onClick={() => togglePin(m.feedbackId, !isPinned)}
                      aria-pressed={isPinned}
                      title={isPinned ? 'Unpin this exchange' : 'Pin this exchange'}
                      className={[
                        'ml-1 shrink-0 text-lg leading-none rounded-md px-2 py-1 border',
                        isPinned
                          ? 'text-amber-600 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-500/15 border-amber-300 dark:border-amber-500/40'
                          : 'text-slate-500 dark:text-slate-400 bg-slate-100/60 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/50 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:border-amber-300 dark:hover:border-amber-500/40',
                      ].join(' ')}
                    >
                      {isPinned ? '📌' : '📍'}
                    </button>
                  )}
                </div>
                <div
                  className={[
                    'group rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed ai-markdown overflow-x-auto',
                    m.error
                      ? 'bg-red-50 dark:bg-red-500/[0.08] text-red-700 dark:text-red-300'
                      : 'bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200',
                  ].join(' ')}
                >
                  {/* GFM gives us tables, strikethrough, task lists. Tables
                      are what the auto-fired "lab summary" query relies on
                      to render colored rows — see clinic.css .ai-markdown.
                      The custom `tr` component reads the row's text content
                      and tags critical/borderline rows so CSS can tint them. */}
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ node, ...props }) => <p className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-2" {...props} />,
                      strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900 dark:text-slate-100" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1.5 my-2 text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-1.5 my-2 text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />,
                      li: ({ node, ...props }) => <li className="text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />,
                      h1: ({ node, ...props }) => <h1 className="text-sm md:text-base font-bold text-slate-900 dark:text-slate-100 mt-3 mb-1.5" {...props} />,
                      h2: ({ node, ...props }) => <h2 className="text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 mt-2.5 mb-1" {...props} />,
                      h3: ({ node, ...props }) => <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-200 mt-2 mb-1" {...props} />,
                      table: ({ node, ...props }) => (
                        <div className="overflow-x-auto w-full my-3 rounded-xl border border-slate-250 dark:border-slate-800/80 bg-white dark:bg-[#0c0c12] shadow-sm">
                          <table className="min-w-full border-collapse text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />
                        </div>
                      ),
                      thead: ({ node, ...props }) => <thead className="bg-slate-50 dark:bg-slate-900/60" {...props} />,
                      th: ({ node, ...props }) => <th className="border-b border-slate-250 dark:border-slate-800 px-3 py-2 font-bold text-left text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900/40" {...props} />,
                      td: ({ node, ...props }) => <td className="border-b border-slate-250 dark:border-slate-800 px-3 py-2 text-slate-700 dark:text-slate-300" {...props} />,
                      tr: ({ children, ...props }) => {
                        const text = childrenToText(children);
                        const cls = text.includes('🚨') ? 'row-critical bg-red-500/10 dark:bg-red-500/20 text-red-950 dark:text-red-200'
                                  : text.includes('⚠️')  ? 'row-warning bg-amber-500/10 dark:bg-amber-500/20 text-amber-950 dark:text-amber-200'
                                  : '';
                        return <tr {...props} className={`${cls} hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors`}>{children}</tr>;
                      },
                    }}
                  >
                    {m.text || ''}
                  </ReactMarkdown>
                </div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-2">
                  {m.time && <span>{m.time}</span>}
                  <span
                    className={[
                      'rounded px-1.5 py-0.5 font-semibold',
                      m.activeSkill
                        ? 'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300'
                        : 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400',
                    ].join(' ')}
                  >
                    Skill: {m.activeSkill ? `Caveman (${m.skillIntensity || 'full'})` : 'None'}
                  </span>
                  {m.sources && m.sources.length > 0 && (
                    <span className="text-slate-500 dark:text-slate-400" title={JSON.stringify(m.sources, null, 2)}>
                      · sources: {m.sources
                        .map(s => {
                          if (typeof s === 'string') return s;
                          // Backend may return either {name}, {source}, {type}, or {title} per source
                          const label = s?.label || s?.name || s?.title || s?.source || s?.type;
                          return label ? String(label) : '?';
                        })
                        .join(', ')}
                    </span>
                  )}
                </div>
                {canPin && (() => {
                  const draft = feedbackDrafts[m.feedbackId];
                  const activeStatus = draft?.status ?? m.status ?? null;
                  if (draft?.submitted) {
                    return (
                      <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">Thanks for the feedback.</div>
                    );
                  }
                  return (
                    <div className="mt-2 flex flex-col gap-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">Was this helpful?</span>
                        <button
                          type="button"
                          aria-label="Helpful"
                          aria-pressed={activeStatus === 'positive'}
                          className={[
                            'rounded-md border px-2 py-0.5 text-xs',
                            activeStatus === 'positive'
                              ? 'border-emerald-600 bg-emerald-600 text-white'
                              : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200',
                          ].join(' ')}
                          onClick={() => chooseFeedbackStatus(m.feedbackId, 'positive')}
                        >
                          👍 Yes
                        </button>
                        <button
                          type="button"
                          aria-label="Not helpful"
                          aria-pressed={activeStatus === 'negative'}
                          className={[
                            'rounded-md border px-2 py-0.5 text-xs',
                            activeStatus === 'negative'
                              ? 'border-rose-600 bg-rose-600 text-white'
                              : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200',
                          ].join(' ')}
                          onClick={() => chooseFeedbackStatus(m.feedbackId, 'negative')}
                        >
                          👎 No
                        </button>
                        {draft?.status && (
                          <>
                            <button
                              type="button"
                              className="ml-1 rounded-md border border-slate-300 px-2 py-0.5 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
                              onClick={() => cancelFeedbackDraft(m.feedbackId)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={draft.submitting}
                              className="rounded-md bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                              onClick={() => submitFeedback(m.feedbackId)}
                            >
                              {draft.submitting ? 'Submitting...' : 'Submit'}
                            </button>
                          </>
                        )}
                      </div>
                      {draft?.status && (
                        <textarea
                          className="min-h-[44px] w-full max-w-md rounded-md border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900"
                          placeholder="Optional: tell us more..."
                          value={draft.text || ''}
                          onChange={(e) => setFeedbackDraftText(m.feedbackId, e.target.value)}
                        />
                      )}
                      {draft?.error && <div className="text-[10px] text-rose-600">{draft.error}</div>}
                    </div>
                  );
                })()}
              </div>
            );
          }
          // User message
          return wrapper(
            <div>
              <div className="flex items-center gap-2 mb-1.5 justify-end">
                {canPin && (
                  <button
                    type="button"
                    onClick={() => togglePin(m.feedbackId, !isPinned)}
                    aria-pressed={isPinned}
                    title={isPinned ? 'Unpin this exchange' : 'Pin this exchange'}
                    className={[
                      'mr-auto shrink-0 text-lg leading-none rounded-md px-2 py-1 border',
                      isPinned
                        ? 'text-amber-600 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-500/15 border-amber-300 dark:border-amber-500/40'
                        : 'text-slate-500 dark:text-slate-400 bg-slate-100/60 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/50 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:border-amber-300 dark:hover:border-amber-500/40',
                    ].join(' ')}
                  >
                    {isPinned ? '📌' : '📍'}
                  </button>
                )}
                <div className="text-[10px] font-bold tracking-widest text-slate-500 dark:text-slate-400">
                  YOU
                </div>
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">
                  {(userInitials || 'U').slice(0, 2).toUpperCase()}
                </div>
              </div>
              <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed ml-auto max-w-[85%] whitespace-pre-wrap">
                {m.text}
              </div>
              {m.time && (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 text-right">{m.time}</div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
            Assistant is thinking…
          </div>
        )}
      </div>

      {/* Suggested chips + input */}
      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800/60">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SUGGESTED_PROMPTS.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => send(p)}
              disabled={loading}
              className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700/60 disabled:opacity-50"
            >
              {p}
            </button>
          ))}
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            send(input);
          }}
          className="relative flex items-center bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/60 rounded-xl pr-2 focus-within:border-blue-400 dark:focus-within:border-blue-500"
        >
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask anything about this patient's medical records..."
            className="flex-1 bg-transparent px-4 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            aria-label="Send"
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-600 text-white disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </form>
      </div>

      {/* Settings Modal — Tailwind, styled to match the clinic page. Password
          gate → tabbed options (OCR Settings + Text Chat). */}
      {isSettingsOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[8vh] pb-4"
          role="dialog"
          aria-modal="true"
          aria-label="AI Assistant Settings"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={closeSettings}
          />
          {/* Panel */}
          <div className="relative w-full max-w-md max-h-[80vh] flex flex-col bg-white dark:bg-[#14141c] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800/60 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800/60 flex items-center justify-between shrink-0">
              <div className="font-extrabold text-base text-slate-900 dark:text-slate-50">
                AI Assistant Settings
              </div>
              <button
                type="button"
                onClick={closeSettings}
                aria-label="Close"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4">
              {!isSettingsUnlocked ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Enter password to access settings:
                  </p>
                  <input
                    type="password"
                    value={settingsPassword}
                    autoFocus
                    onChange={(e) => {
                      setSettingsPassword(e.target.value);
                      setSettingsPasswordError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSettingsPasswordSubmit();
                    }}
                    placeholder="Password"
                    className={[
                      'w-full text-sm rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 border focus:outline-none',
                      settingsPasswordError
                        ? 'border-red-400 dark:border-red-500/60 focus:border-red-500'
                        : 'border-slate-200 dark:border-slate-700/60 focus:border-blue-400 dark:focus:border-blue-500',
                    ].join(' ')}
                  />
                  {settingsPasswordError && (
                    <p className="text-xs text-red-500 dark:text-red-400">{settingsPasswordError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleSettingsPasswordSubmit}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
                  >
                    Unlock Settings
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Tab switcher */}
                  <div className="flex gap-1.5">
                    {[
                      { key: 'ocr', label: 'OCR Settings' },
                      { key: 'chat', label: 'Text Chat' },
                      { key: 'skills', label: 'Skills' },
                      { key: 'sources', label: 'Default Sources' },
                    ].map((t) => {
                      const active = settingsTab === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setSettingsTab(t.key)}
                          aria-pressed={active}
                          className={[
                            'text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors',
                            active
                              ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-500/40'
                              : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700/60',
                          ].join(' ')}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>

                  {settingsTab === 'ocr' ? (
                    <div className="space-y-5">
                      {/* Processing Speed */}
                      <div>
                        <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-2">
                          Processing Speed
                        </div>
                        <div className="space-y-1.5">
                          {SPEED_OPTIONS.map((opt) => {
                            const active = speedSetting === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setSpeedSetting(opt.value)}
                                aria-pressed={active}
                                className={[
                                  'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                  active
                                    ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/[0.08] text-blue-800 dark:text-blue-200'
                                    : 'border-slate-200 dark:border-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40',
                                ].join(' ')}
                              >
                                <span
                                  className={[
                                    'w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0',
                                    active ? 'border-blue-500' : 'border-slate-300 dark:border-slate-600',
                                  ].join(' ')}
                                >
                                  {active && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                                </span>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {speedSetting === 'advanced' && (
                        <div className="space-y-3">
                          <div>
                            <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-1.5">
                              Max Characters Per Document
                            </div>
                            <input
                              type="number"
                              min={1000}
                              max={10000000}
                              value={customMaxCharsPerDoc}
                              onChange={(e) => setCustomMaxCharsPerDoc(parseInt(e.target.value) || 5000)}
                              className="w-full text-sm rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700/60 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-1.5">
                              Max Total Content Length
                            </div>
                            <input
                              type="number"
                              min={10000}
                              max={10000000}
                              value={customMaxTotalLength}
                              onChange={(e) => setCustomMaxTotalLength(parseInt(e.target.value) || 50000)}
                              className="w-full text-sm rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700/60 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500"
                            />
                          </div>
                        </div>
                      )}

                      {/* OCR Model Priority */}
                      <div>
                        <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-1">
                          OCR Model Priority
                        </div>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-2.5">
                          Select up to 3 models. If the first fails, the next one will be used.
                        </p>
                        {isLoadingModels ? (
                          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <span className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600 border-t-blue-500 animate-spin" />
                            Loading models…
                          </div>
                        ) : modelLoadError ? (
                          <p className="text-sm text-red-500 dark:text-red-400">
                            Failed to load models: {modelLoadError}
                          </p>
                        ) : (
                          <div className="space-y-2.5">
                            {[0, 1, 2].map((index) => (
                              <div key={index}>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                                  Priority {index + 1}
                                </div>
                                <select
                                  value={selectedModels[index] || ''}
                                  onChange={(e) => {
                                    const newModels = [...selectedModels];
                                    newModels[index] = e.target.value;
                                    setSelectedModels(newModels);
                                  }}
                                  className="w-full text-sm rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700/60 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500"
                                >
                                  <option value="">Select a model</option>
                                  {availableModels.map((model) => (
                                    <option key={model.model_id} value={model.model_id}>
                                      {model.config?.display_name || model.model_id}
                                      {model.config?.provider ? ` (${model.config.provider})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={resetOcrModels}
                          className="mt-3 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                        >
                          Reset to Default
                        </button>
                      </div>
                    </div>
                  ) : settingsTab === 'chat' ? (
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-1">
                        Chat Model
                      </div>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-2.5">
                        Select the built-in model to use for text chat queries.
                      </p>
                      {chatModelCatalog.loading && !chatModelSelect.options.length ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">Loading the newest models…</p>
                      ) : !chatModelSelect.options.length ? (
                        <p className="text-sm text-red-500 dark:text-red-400">
                          Failed to load chat models: {chatModelCatalog.error || 'no models returned'}
                        </p>
                      ) : (
                      <select
                        value={chatModelSelect.current}
                        onChange={(e) => setChatModel(coerceChatModel(e.target.value) ?? chatModel)}
                        className="w-full text-sm rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700/60 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500"
                      >
                        {chatModelSelect.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      )}
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5">
                        Newest Claude models, fetched live from Anthropic.
                      </p>
                    </div>
                  ) : settingsTab === 'skills' ? (
                    <div className="space-y-5">
                      <div>
                        <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-1">
                          Skill
                        </div>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-2.5">
                          Change how the assistant communicates. Applies to every chat reply while active.
                        </p>
                        <div className="space-y-1.5">
                          {[
                            { value: null, label: 'None (default)' },
                            { value: 'caveman', label: 'Caveman (ultra-terse replies)' },
                          ].map((opt) => {
                            const active = activeSkill === opt.value;
                            return (
                              <button
                                key={opt.label}
                                type="button"
                                onClick={() => { skillTouchedRef.current = true; setActiveSkill(opt.value); }}
                                aria-pressed={active}
                                className={[
                                  'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                  active
                                    ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/[0.08] text-blue-800 dark:text-blue-200'
                                    : 'border-slate-200 dark:border-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40',
                                ].join(' ')}
                              >
                                <span
                                  className={[
                                    'w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0',
                                    active ? 'border-blue-500' : 'border-slate-300 dark:border-slate-600',
                                  ].join(' ')}
                                >
                                  {active && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                                </span>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {activeSkill === 'caveman' && (
                        <div>
                          <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-2">
                            Intensity
                          </div>
                          <div className="space-y-1.5">
                            {[
                              { value: 'lite', label: 'Lite — tight but full sentences' },
                              { value: 'full', label: 'Full — classic caveman (default)' },
                              { value: 'ultra', label: 'Ultra — maximum compression' },
                            ].map((opt) => {
                              const active = skillIntensity === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => { skillTouchedRef.current = true; setSkillIntensity(opt.value); }}
                                  aria-pressed={active}
                                  className={[
                                    'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                    active
                                      ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/[0.08] text-blue-800 dark:text-blue-200'
                                      : 'border-slate-200 dark:border-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40',
                                  ].join(' ')}
                                >
                                  <span
                                    className={[
                                      'w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0',
                                      active ? 'border-blue-500' : 'border-slate-300 dark:border-slate-600',
                                    ].join(' ')}
                                  >
                                    {active && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                                  </span>
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mb-1">
                        Default Sources
                      </div>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-2.5">
                        Sources turned on automatically each time a patient loads.
                      </p>
                      <div className="space-y-1.5">
                        {ALL_SOURCES.map((src) => {
                          const on = effectiveDefaultSources.includes(src.key);
                          return (
                            <button
                              key={src.key}
                              type="button"
                              onClick={() => toggleDefaultSource(src.key)}
                              aria-pressed={on}
                              className={[
                                'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                on
                                  ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/[0.08] text-blue-800 dark:text-blue-200'
                                  : 'border-slate-200 dark:border-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40',
                              ].join(' ')}
                            >
                              <span
                                className={[
                                  'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                                  on
                                    ? 'bg-blue-500 border-blue-500 text-white'
                                    : 'border-slate-300 dark:border-slate-600',
                                ].join(' ')}
                              >
                                {on && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              {src.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {settingsNotice && (
                    <div className="text-xs font-semibold text-blue-800 dark:text-blue-200 bg-blue-100 dark:bg-blue-500/15 rounded-md px-3 py-2">
                      {settingsNotice}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={saveSettings}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
                  >
                    Save Settings
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
